import { GetContextRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import type { ContextResponse, GrantOp, ProjectId, ScopeKey } from "@storytell/types";
import { createResource, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

export type ActiveContextStore = ReturnType<typeof createActiveContextStore>;

// The project id an organization-wide grant carries. That row holds the project
// wildcard, which is not an id, so the server sends the zero project id in its
// place rather than omitting the field — a well-formed id that matches no
// project. Comparing it to the focused project is what would drop the event.
const ORG_WIDE_PROJECT_ID = "project_00000000000000000000";

const scopeKey = (s: { projectId: ProjectId | undefined; orgId: string | undefined }) =>
  `${s.projectId ?? ""}|${s.orgId ?? ""}`;

// Hydrates the capability matrix for the project or org the URL points at.
// Fast navigation: a monotonic fetch counter discards superseded results so
// a slow earlier response can't overwrite the current scope's matrix.
export const createActiveContextStore = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => ProjectId | undefined;
  orgId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  // Preserve last-good across transient failures so callers don't gate on an
  // empty matrix and lock real collaborators out. Keyed by scope: serving a
  // previous project's matrix would silently authorize against the wrong roles.
  let lastSuccessful: { key: string; value: ContextResponse } | undefined;
  // Counter instead of AbortController: aborting resolves the prior fetch to
  // undefined, briefly overwriting the resource before the new fetch lands —
  // long enough for downstream memos to read an empty matrix.
  let latestFetchId = 0;

  const fallbackFor = (source: { projectId: ProjectId | undefined; orgId: string | undefined }) =>
    lastSuccessful?.key === scopeKey(source) ? lastSuccessful.value : undefined;

  const [contextData, { refetch }] = createResource(
    () => {
      if (!deps.authReady()) return false;
      const projectId = deps.projectId();
      const orgId = deps.orgId();
      if (!projectId && !orgId) return false;
      return { projectId, orgId };
    },
    async (source: {
      projectId: ProjectId | undefined;
      orgId: string | undefined;
    }): Promise<ContextResponse | undefined> => {
      const myFetchId = ++latestFetchId;
      const request = GetContextRequestConfig.withBearerToken(deps.authToken());
      // orgId rides the global `getOrgId` config on storytellClient and gets
      // stamped automatically alongside the project override when applicable.
      const response = await client.fetch(request, undefined, undefined, {
        projectIdOverride: source.projectId,
      });
      if (myFetchId !== latestFetchId) return fallbackFor(source);
      if (response.isErr()) return fallbackFor(source);
      lastSuccessful = { key: scopeKey(source), value: response.value };
      return response.value;
    },
    { deferStream: true }
  );

  if (!isServer) {
    onMount(() => {
      const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
        void refetch();
      });

      // Auth recovery — retry a previously-failing fetch. Uses the dedicated
      // event rather than refresh-sync, which fires on every content mutation.
      const unsubscribeAuthRefresh = subscribeToGlobalEvent("auth-token-refreshed", () => {
        void refetch();
      });

      // Any grant change invalidates the cached capability matrix from
      // /context. Every cp.grants event is addressed to the holder of the row
      // it describes, so one arriving here always concerns this member's own
      // grants — an accepted invitation as much as a role change or a revoke.
      // No op is filtered out for that reason.
      //
      // An organization-wide change names no project — it rides the wildcard
      // grant row, which reaches every project in the organization, including
      // the focused one — so only a change scoped to some other project can be
      // ignored here.
      const unsubscribeServerEvent = subscribeToServerEvent("cp.grants", (serverEvent) => {
        const eventProjectId = serverEvent.resource.projectId;
        const namesAnotherProject =
          !!eventProjectId && eventProjectId !== ORG_WIDE_PROJECT_ID && eventProjectId !== deps.projectId();
        if (namesAnotherProject) return;
        void refetch();
      });

      onCleanup(() => {
        unsubscribeConnectivityRestored();
        unsubscribeAuthRefresh();
        unsubscribeServerEvent();
      });
    });
  }

  const activeContext = () => contextData.latest;

  const canAt = (scope: ScopeKey, resource: string, op: GrantOp): boolean => {
    const ctx = contextData.latest;
    const ops = ctx?.capabilities?.[resource]?.[scope];
    return Array.isArray(ops) && ops.includes(op);
  };

  // True only when the cached matrix matches the current URL scope; during
  // fast nav the resource may still hold the previous scope's matrix and
  // gates must wait rather than authorize against the wrong roles.
  const isLoadedForCurrentScope = (): boolean => {
    if (!contextData.latest || !lastSuccessful) return false;
    const projectId = deps.projectId();
    const orgId = deps.orgId();
    if (!projectId && !orgId) return false;
    return lastSuccessful.key === scopeKey({ projectId, orgId });
  };

  return {
    refetch,
    activeContext,
    canAt,
    isLoadedForCurrentScope,
  };
};
